import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Divider,
    IconButton,
    Stack,
    Switch,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { getHomepageConfig, updateHomepageConfig } from '../api/homepage';

function normalizeTag(tag) {
    return String(tag || '').trim().toLowerCase();
}

export default function HomePageAdminPage() {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const [sections, setSections] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        setErr('');
        try {
            const cfg = await getHomepageConfig();
            const list = Array.isArray(cfg?.sections) ? cfg.sections : [];
            setSections(
                list.map((s) => ({
                    title: s?.title ?? '',
                    tag: s?.tag ?? '',
                    image: s?.image ?? '',
                    active: Boolean(s?.active),
                }))
            );
        } catch (e) {
            setErr(e?.response?.data?.message || e?.message || 'Load failed');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const onChangeField = (idx, patch) => {
        setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    };

    const move = (from, to) => {
        setSections((prev) => {
            if (to < 0 || to >= prev.length) return prev;
            const next = [...prev];
            const [item] = next.splice(from, 1);
            next.splice(to, 0, item);
            return next;
        });
    };

    const addSection = () => {
        setSections((prev) => [
            ...prev,
            { title: 'New Section', tag: 'tag', image: '', active: true },
        ]);
    };

    const removeSection = (idx) => {
        if (!window.confirm('Remove this section?')) return;
        setSections((prev) => prev.filter((_, i) => i !== idx));
    };

    const canSave = useMemo(() => {
        if (!sections.length) return true;
        // validate: title + tag required
        for (const s of sections) {
            if (!String(s.title || '').trim()) return false;
            if (!normalizeTag(s.tag)) return false;
        }
        return true;
    }, [sections]);

    const save = async () => {
        if (!canSave) {
            alert('Please fill all section title and tag.');
            return;
        }

        setSaving(true);
        setErr('');
        try {
            const payload = sections.map((s) => {
                const tag = normalizeTag(s.tag);
                return {
                    title: String(s.title || '').trim(),
                    tag,
                    active: Boolean(s.active),
                    // image optional: nếu bạn muốn giữ image theo tag, có thể set default ở backend
                    image: String(s.image || '').trim() || `sections/${tag}.png`,
                };
            });

            await updateHomepageConfig(payload);
            await load();
        } catch (e) {
            setErr(e?.response?.data?.message || e?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h5">Home page sections</Typography>

                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addSection}
                        disabled={saving}
                    >
                        Add
                    </Button>

                    <Button
                        variant="contained"
                        onClick={save}
                        disabled={saving || !canSave}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Stack>
            </Stack>

            {err && (
                <Box sx={{ mb: 2, color: 'error.main' }}>
                    {err}
                </Box>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={1.5}>
                    {sections.map((s, idx) => (
                        <Box
                            key={`${s.tag}-${idx}`}
                            sx={{ border: '1px solid #eee', borderRadius: 1, p: 1.5 }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ width: 26, color: 'text.secondary' }}>
                                    {idx + 1}
                                </Typography>

                                <TextField
                                    size="small"
                                    label="Title"
                                    value={s.title}
                                    onChange={(e) => onChangeField(idx, { title: e.target.value })}
                                    sx={{ flex: 1 }}
                                    disabled={saving}
                                />

                                <TextField
                                    size="small"
                                    label="Tag"
                                    value={s.tag}
                                    onChange={(e) => onChangeField(idx, { tag: e.target.value })}
                                    sx={{ width: 200 }}
                                    disabled={saving}
                                />

                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: 90, justifyContent: 'center' }}>
                                    <Typography variant="caption">Active</Typography>
                                    <Switch
                                        checked={Boolean(s.active)}
                                        onChange={(e) => onChangeField(idx, { active: e.target.checked })}
                                        disabled={saving}
                                        size="small"
                                    />
                                </Stack>

                                <IconButton
                                    size="small"
                                    onClick={() => move(idx, idx - 1)}
                                    disabled={saving || idx === 0}
                                    title="Move up"
                                >
                                    <ArrowUpwardIcon fontSize="small" />
                                </IconButton>

                                <IconButton
                                    size="small"
                                    onClick={() => move(idx, idx + 1)}
                                    disabled={saving || idx === sections.length - 1}
                                    title="Move down"
                                >
                                    <ArrowDownwardIcon fontSize="small" />
                                </IconButton>

                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeSection(idx)}
                                    disabled={saving}
                                    title="Remove"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Stack>

                            <Divider sx={{ my: 1 }} />

                            <Typography variant="caption" color="text.secondary">
                                image/path will be built from tag on API:
                                {' '}
                                <b>{`sections/${normalizeTag(s.tag)}.png`}</b>
                                {'  |  '}
                                <b>{`/mcpeaddons?tag=${encodeURIComponent(normalizeTag(s.tag))}`}</b>
                            </Typography>
                        </Box>
                    ))}

                    {sections.length === 0 && (
                        <Box sx={{ p: 2, border: '1px dashed #ddd', borderRadius: 1, color: 'text.secondary' }}>
                            No sections
                        </Box>
                    )}
                </Stack>
            )}
        </Container>
    );
}